/*
 * Rebecca Ray — Launch PDP enhancements
 * 1. Mobile media carousel pagination dots (kept in sync when the variant
 *    swap swaps the gallery <li> slides via VariantSelects.updateMedia()).
 * 2. Sticky add-to-cart bar (mobile): mirrors the main button's sold-out
 *    state + label and the live price, appears once the main buy area
 *    scrolls out of view, and forwards clicks to the real submit button.
 * 3. Gallery image fade-in: marks each .media > img .is-loaded on load so
 *    launch.css can opacity-fade (Dawn's easing); re-scans on gallery DOM
 *    swaps from variant change.
 *
 * Dependency-free: relies only on MutationObserver + IntersectionObserver so
 * it stays in sync with Dawn's native variant-change re-render.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMql = window.matchMedia('(max-width: 989px)');

  function scrollBehavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  /* ————————————————— Gallery image fade-in ————————————————— */
  function markImageLoaded(img) {
    if (img.classList.contains('is-loaded')) return;
    var done = function () {
      img.classList.add('is-loaded');
    };
    if (img.complete && img.naturalWidth > 0) {
      // Double rAF so cached images still get a short fade, not a hard pop.
      requestAnimationFrame(function () {
        requestAnimationFrame(done);
      });
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }

  function initImageFade(section) {
    var media = section.querySelector('.rr-pdp__media');
    if (!media) return;

    function scan() {
      media.querySelectorAll('.media > img').forEach(markImageLoaded);
    }

    scan();
    new MutationObserver(scan).observe(media, { childList: true, subtree: true });
  }

  /* ————————————————— Mobile carousel dots (Dawn markup) ————————————————— */
  function initDots(section) {
    var list = section.querySelector('.rr-pdp__media-list');
    var dotsWrap = section.querySelector('[data-rr-dots]');
    var dotsInner = section.querySelector('[data-rr-dots-wrap]');
    if (!list || !dotsWrap || !dotsInner) return;

    var slides = [];
    var dotLinks = [];

    function activeIndex() {
      var mid = list.scrollLeft + list.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var center = slides[i].offsetLeft + slides[i].clientWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    function setActive(index) {
      for (var i = 0; i < dotLinks.length; i++) {
        var on = i === index;
        dotLinks[i].classList.toggle('slider-counter__link--active', on);
        dotLinks[i].setAttribute('aria-current', on ? 'true' : 'false');
      }
    }

    function build() {
      slides = Array.prototype.slice.call(list.querySelectorAll('li'));
      dotsInner.innerHTML = '';
      dotLinks = [];
      if (slides.length <= 1) {
        dotsWrap.hidden = true;
        return;
      }
      dotsWrap.hidden = false;
      slides.forEach(function (slide, i) {
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'slider-counter__link slider-counter__link--dots link';
        link.setAttribute(
          'aria-label',
          'View image ' + (i + 1) + ' of ' + slides.length
        );
        link.innerHTML = '<span class="dot"></span>';
        link.addEventListener('click', function () {
          list.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
          setActive(i);
        });
        dotsInner.appendChild(link);
        dotLinks.push(link);
      });
      setActive(activeIndex());
    }

    var scrollTimer;
    list.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          setActive(activeIndex());
        }, 60);
      },
      { passive: true }
    );

    // Rebuild when the gallery slides are swapped on variant change.
    var mo = new MutationObserver(function () {
      build();
    });
    mo.observe(list, { childList: true });

    build();
  }

  /* ————————————————— Sticky add-to-cart bar ————————————————— */
  function initSticky(section) {
    var bar = section.querySelector('[data-rr-sticky]');
    if (!bar) return;
    var sectionId = bar.getAttribute('data-section');
    var mainBtn = document.getElementById('ProductSubmitButton-' + sectionId);
    var priceEl = document.getElementById('price-' + sectionId);
    var buyArea = section.querySelector('.rr-pdp__buy');
    var stickyBtn = bar.querySelector('[data-rr-sticky-add]');
    var stickyLabel = bar.querySelector('[data-rr-sticky-label]');
    var stickyPrice = bar.querySelector('[data-rr-sticky-price]');
    if (!mainBtn || !stickyBtn) return;

    function sync() {
      var disabled = mainBtn.hasAttribute('disabled');
      stickyBtn.disabled = disabled;
      stickyBtn.classList.toggle('is-disabled', disabled);
      var mainLabel = mainBtn.querySelector('span');
      if (mainLabel && stickyLabel) stickyLabel.textContent = mainLabel.textContent.trim();
      if (priceEl && stickyPrice) {
        var amount = priceEl.querySelector('.rr-pdp__price-amount');
        if (amount) stickyPrice.textContent = amount.textContent.trim();
      }
    }

    // Mirror the main button's disabled/label + the live price on every change.
    new MutationObserver(sync).observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });
    if (priceEl) {
      new MutationObserver(sync).observe(priceEl, { childList: true, subtree: true });
    }
    sync();

    stickyBtn.addEventListener('click', function () {
      if (!stickyBtn.disabled) mainBtn.click();
    });

    var scrolledPastBuy = false;
    function updateVisibility() {
      bar.hidden = !scrolledPastBuy || !mobileMql.matches;
    }

    if (buyArea && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var rect = entries[0].boundingClientRect;
          // Show sticky only after the buy block has scrolled off the top —
          // not while it is still below the fold (e.g. media still on screen).
          scrolledPastBuy = rect.bottom < 0;
          updateVisibility();
        },
        { threshold: [0, 0.01, 1] }
      ).observe(buyArea);
    } else if (buyArea) {
      var onScroll = function () {
        scrolledPastBuy = buyArea.getBoundingClientRect().bottom < 0;
        updateVisibility();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    if (mobileMql.addEventListener) {
      mobileMql.addEventListener('change', updateVisibility);
    } else if (mobileMql.addListener) {
      mobileMql.addListener(updateVisibility);
    }
    updateVisibility();
  }

  /* ————————————————— Live selected colour name ————————————————— */
  function initSwatchLabel(section) {
    var valueEl = section.querySelector('[data-rr-swatch-value]');
    var vs = section.querySelector('variant-selects');
    if (!valueEl || !vs) return;
    vs.addEventListener('change', function (event) {
      var target = event.target;
      if (target && target.name === 'Color' && target.value) {
        valueEl.textContent = target.value;
      }
    });
  }

  /* ————————————————— Description “See more” ————————————————— */
  function initDescription(section) {
    var wrap = section.querySelector('[data-rr-desc]');
    if (!wrap || !wrap.hasAttribute('data-rr-desc-truncated')) return;

    var shortEl = wrap.querySelector('[data-rr-desc-short]');
    var fullEl = wrap.querySelector('[data-rr-desc-full]');
    var btn = wrap.querySelector('[data-rr-see-more]');
    if (!shortEl || !fullEl || !btn) return;

    var mobileOnly = wrap.hasAttribute('data-rr-desc-mobile-only');
    var expanded = false;

    function apply() {
      // Mobile: always start truncated when markup is present.
      // Desktop: if mobile-only truncate, show full; otherwise honor expanded state.
      if (mobileOnly && !mobileMql.matches) {
        shortEl.hidden = true;
        fullEl.hidden = false;
        btn.hidden = true;
        return;
      }

      if (!expanded) {
        shortEl.hidden = false;
        fullEl.hidden = true;
        btn.hidden = false;
        btn.textContent = 'See more';
        btn.setAttribute('aria-expanded', 'false');
      } else {
        shortEl.hidden = true;
        fullEl.hidden = false;
        btn.hidden = false;
        btn.textContent = 'See less';
        btn.setAttribute('aria-expanded', 'true');
      }
    }

    btn.addEventListener('click', function () {
      expanded = !expanded;
      apply();
    });

    if (mobileMql.addEventListener) {
      mobileMql.addEventListener('change', function () {
        if (mobileMql.matches) expanded = false;
        apply();
      });
    } else if (mobileMql.addListener) {
      mobileMql.addListener(function () {
        if (mobileMql.matches) expanded = false;
        apply();
      });
    }

    apply();
  }

  function init() {
    document.querySelectorAll('.rr-pdp-section').forEach(function (section) {
      initImageFade(section);
      initDots(section);
      initSticky(section);
      initSwatchLabel(section);
      initDescription(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
